import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(3),
  },
}));

const OnSpaceSettings = ({ settings = [] }) => {
  const classes = useStyles();
  const { update } = useSettings();

  const [onspaceUrl, setOnspaceUrl] = useState("");
  const [onspaceApiKey, setOnspaceApiKey] = useState("");
  const [onspaceSalaoId, setOnspaceSalaoId] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!Array.isArray(settings)) return;

    const urlSetting = settings.find((s) => s.key === "onspaceUrl");
    const apiKeySetting = settings.find((s) => s.key === "onspaceApiKey");
    const salaoIdSetting = settings.find((s) => s.key === "onspaceSalaoId");

    if (urlSetting?.value) setOnspaceUrl(urlSetting.value);
    if (apiKeySetting?.value) setOnspaceApiKey(apiKeySetting.value);
    if (salaoIdSetting?.value) setOnspaceSalaoId(salaoIdSetting.value);
  }, [settings]);

  const handleSave = async () => {
    if (!onspaceSalaoId.trim() || isNaN(Number(onspaceSalaoId.trim()))) {
      toast.error(i18n.t("settings.onSpace.toasts.salaoIdInvalid"));
      return;
    }

    setLoading(true);
    try {
      await Promise.all([
        update({ key: "onspaceUrl", value: onspaceUrl.trim() }),
        update({ key: "onspaceApiKey", value: onspaceApiKey.trim() }),
        update({ key: "onspaceSalaoId", value: onspaceSalaoId.trim() }),
      ]);
      toast.success(i18n.t("settings.onSpace.toasts.saved"));
    } catch (error) {
      toast.error(i18n.t("settings.onSpace.toasts.saveError"));
      console.error("[OnSpaceSettings] Erro:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {i18n.t("settings.onSpace.title")}
      </Typography>
      <Typography className={classes.subtitle}>
        {i18n.t("settings.onSpace.description")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={i18n.t("settings.onSpace.fields.url")}
            placeholder={i18n.t("settings.onSpace.fields.urlPlaceholder")}
            value={onspaceUrl}
            onChange={(e) => setOnspaceUrl(e.target.value)}
            helperText={i18n.t("settings.onSpace.fields.urlHelper")}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={`${i18n.t("settings.onSpace.fields.salaoId")} *`}
            placeholder={i18n.t("settings.onSpace.fields.salaoIdPlaceholder")}
            value={onspaceSalaoId}
            onChange={(e) => setOnspaceSalaoId(e.target.value.replace(/[^0-9]/g, ""))}
            helperText={i18n.t("settings.onSpace.fields.salaoIdHelper")}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            className={classes.field}
            fullWidth
            variant="outlined"
            size="small"
            label={i18n.t("settings.onSpace.fields.apiKey")}
            placeholder={i18n.t("settings.onSpace.fields.apiKeyPlaceholder")}
            type={showApiKey ? "text" : "password"}
            value={onspaceApiKey}
            onChange={(e) => setOnspaceApiKey(e.target.value)}
            helperText={i18n.t("settings.onSpace.fields.apiKeyHelper")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowApiKey((prev) => !prev)}
                    edge="end"
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleSave}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? i18n.t("common.saving") : i18n.t("common.save")}
        </Button>
      </div>
    </div>
  );
};

export default OnSpaceSettings;
